import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { AlertCircle, Upload, Sparkles, Loader2, X, Image as ImageIcon } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { Workflow } from '@/types/workflow';

interface ImageManagerTabProps {
  workflow: Workflow;
  onWorkflowUpdate: (workflow: Workflow) => void;
}

type ImageTarget = 'workflow' | 'step';

interface ImageModel {
  id: string;
  name: string;
  description: string;
  provider: string;
}

export function ImageManagerTab({ workflow, onWorkflowUpdate }: ImageManagerTabProps) {
  const [imageTarget, setImageTarget] = useState<ImageTarget>('workflow');
  const [selectedStepId, setSelectedStepId] = useState<string>('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Generation controls
  const [models, setModels] = useState<ImageModel[]>([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(1024);
  const [steps, setSteps] = useState(20);
  const [seed, setSeed] = useState<number | undefined>(undefined);

  // State
  const [generatedImageUrl, setGeneratedImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Load models on mount
  useEffect(() => {
    loadModels();
  }, []);

  // Auto-select first model when models load
  useEffect(() => {
    if (models.length > 0 && !selectedModel) {
      setSelectedModel(models[0]?.id || '');
    }
  }, [models, selectedModel]);

  // Auto-select first step when switching to step target
  useEffect(() => {
    if (imageTarget === 'step' && workflow.definition?.steps?.length && workflow.definition.steps.length > 0 && !selectedStepId) {
      setSelectedStepId(workflow.definition.steps[0]?.id || '');
    }
  }, [imageTarget, workflow.definition?.steps, selectedStepId]);

  const loadModels = async () => {
    try {
      const response = await adminApi.getImageModels();
      setModels(response.models);
    } catch (err: any) {
      setError('Failed to load image models: ' + (err.message || 'Unknown error'));
    }
  };

  const handleFileChange = (file: File | null) => {
    if (!file) {
      setUploadFile(null);
      return;
    }

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload PNG, JPG, WEBP, or GIF');
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File too large. Maximum size is 10MB');
      return;
    }

    setUploadFile(file);
    setError('');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setError('');
    setGeneratedImageUrl('');

    try {
      const result = await adminApi.uploadAsset(uploadFile);
      setGeneratedImageUrl(result.url);
      setUploadFile(null);
    } catch (err: any) {
      setError('Failed to upload image: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter an image prompt');
      return;
    }

    if (!selectedModel) {
      setError('Please select a model');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedImageUrl('');

    try {
      const result = await adminApi.generateImage({
        prompt,
        model: selectedModel,
        width,
        height,
        steps,
        seed,
        negativePrompt: negativePrompt || undefined,
      });

      setGeneratedImageUrl(result.url);
    } catch (err: any) {
      setError('Failed to generate image: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApply = () => {
    if (!generatedImageUrl) {
      setError('No image to apply');
      return;
    }

    try {
      if (imageTarget === 'workflow') {
        // Apply to workflow cover image
        onWorkflowUpdate({
          ...workflow,
          imageUrl: generatedImageUrl,
        });
      } else {
        // Apply to step image
        if (!selectedStepId) {
          setError('Please select a step');
          return;
        }

        const updatedSteps = workflow.definition?.steps?.map((step) =>
          step.id === selectedStepId
            ? { ...step, imageUrl: generatedImageUrl }
            : step
        );

        onWorkflowUpdate({
          ...workflow,
          definition: {
            ...workflow.definition,
            steps: updatedSteps || [],
          },
        });
      }

      setError('');
      alert('Image applied successfully!');
      setGeneratedImageUrl('');
    } catch (err: any) {
      setError('Failed to apply image: ' + err.message);
    }
  };

  const handleRemove = () => {
    try {
      if (imageTarget === 'workflow') {
        onWorkflowUpdate({
          ...workflow,
          imageUrl: undefined,
        });
      } else {
        if (!selectedStepId) {
          setError('Please select a step');
          return;
        }

        const updatedSteps = workflow.definition?.steps?.map((step) =>
          step.id === selectedStepId
            ? { ...step, imageUrl: undefined }
            : step
        );

        onWorkflowUpdate({
          ...workflow,
          definition: {
            ...workflow.definition,
            steps: updatedSteps || [],
          },
        });
      }

      alert('Image removed successfully!');
    } catch (err: any) {
      setError('Failed to remove image: ' + err.message);
    }
  };

  const getCurrentImageUrl = (): string | undefined => {
    if (imageTarget === 'workflow') {
      // Only return string URLs, not AssetImage objects
      return typeof workflow.imageUrl === 'string' ? workflow.imageUrl : undefined;
    } else {
      const step = workflow.definition?.steps?.find((s) => s.id === selectedStepId);
      // Only return string URLs, not AssetImage objects
      return typeof step?.imageUrl === 'string' ? step.imageUrl : undefined;
    }
  };

  const currentImageUrl = getCurrentImageUrl();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Image Manager
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Image Target Selection */}
          <div className="space-y-3">
            <Label>Image Target</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={imageTarget === 'workflow' ? 'default' : 'outline'}
                onClick={() => setImageTarget('workflow')}
                className="flex-1"
              >
                Workflow Cover Image
              </Button>
              <Button
                type="button"
                variant={imageTarget === 'step' ? 'default' : 'outline'}
                onClick={() => setImageTarget('step')}
                className="flex-1"
              >
                Step Image
              </Button>
            </div>

            {/* Step Selection (only shown when target is step) */}
            {imageTarget === 'step' && (
              <div className="space-y-2 mt-3">
                <Label htmlFor="stepSelect">Select Step</Label>
                <Select value={selectedStepId} onValueChange={setSelectedStepId}>
                  <SelectTrigger id="stepSelect">
                    <SelectValue placeholder="Select a step" />
                  </SelectTrigger>
                  <SelectContent>
                    {workflow.definition?.steps?.map((step) => (
                      <SelectItem key={step.id} value={step.id}>
                        {step.id}: {step.question}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Current Image Display */}
          {currentImageUrl && (
            <div className="space-y-2">
              <Label>Current Image</Label>
              <div className="relative border rounded-lg overflow-hidden">
                <img src={currentImageUrl} alt="Current" className="w-full h-auto" />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleRemove}
                  className="absolute top-2 right-2"
                >
                  <X className="h-4 w-4" />
                  Remove
                </Button>
              </div>
            </div>
          )}

          {/* Upload Section */}
          <div className="space-y-3">
            <Label>Upload Image</Label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDragging ? 'border-primary bg-primary/5' : 'border-border'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                Drag and drop an image here, or click to browse
              </p>
              <Input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                className="max-w-xs mx-auto"
              />
              {uploadFile && (
                <p className="text-sm text-green-600 mt-2">
                  Selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>
            <Button
              onClick={handleUpload}
              disabled={!uploadFile || isUploading}
              className="w-full"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Image
                </>
              )}
            </Button>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or Generate</span>
            </div>
          </div>

          {/* Generation Section */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger id="model">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {models.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prompt">Prompt</Label>
              <Textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[100px]"
                placeholder="Describe the image you want to generate..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="negativePrompt">Negative Prompt (Optional)</Label>
              <Textarea
                id="negativePrompt"
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                className="min-h-[60px]"
                placeholder="Describe what you don't want in the image..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="width">Width</Label>
                <Input
                  id="width"
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(parseInt(e.target.value) || 1024)}
                  min={256}
                  max={2048}
                  step={64}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="height">Height</Label>
                <Input
                  id="height"
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(parseInt(e.target.value) || 1024)}
                  min={256}
                  max={2048}
                  step={64}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="steps">Steps</Label>
                <Input
                  id="steps"
                  type="number"
                  value={steps}
                  onChange={(e) => setSteps(parseInt(e.target.value) || 20)}
                  min={1}
                  max={100}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="seed">Seed (Optional)</Label>
                <Input
                  id="seed"
                  type="number"
                  value={seed ?? ''}
                  onChange={(e) => setSeed(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Random"
                />
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={isGenerating || !selectedModel}
              className="w-full"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Image
                </>
              )}
            </Button>
          </div>

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Generated/Uploaded Image Preview */}
          {generatedImageUrl && (
            <div className="space-y-2">
              <Label>New Image Preview</Label>
              <div className="border rounded-lg overflow-hidden">
                <img src={generatedImageUrl} alt="Generated/Uploaded" className="w-full h-auto" />
              </div>
              <Button onClick={handleApply} className="w-full" size="lg">
                Apply to {imageTarget === 'workflow' ? 'Workflow' : 'Step'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
